import { ChangeEvent, Ref, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BodyText,
  Box,
  FieldData,
  MaterialSymbol,
  SwitchIndicator,
  Tooltip,
  useForm,
  withStyles,
  type PermissionsMap,
} from 'vopay';
import { Title, UtilButton } from '#shared/components/UI';
import CollapsibleList from './CollapsibleList/CollapsibleList';
import styles from './UserPermissionsForm.module.scss';

const classes = withStyles(styles);

export type PermissionFormValues = Record<string, boolean>;

export type UserPermissionsFormRef = {
  toggleAll: (enabled: boolean) => void;
};

type UserPermissionsFormProps = {
  permissions: PermissionsMap;
  onSubmit: (values: PermissionFormValues) => void;
  ref: Ref<UserPermissionsFormRef>;
  onFormStateChange: (hasChanges: boolean, isValid: boolean) => void;
  onAllEnabledChange?: (allEnabled: boolean) => void;
  /** Form id for the footer submit button (`form="..."`). */
  id: string;
  /** Filters the list; the parent owns the value and renders the search field. */
  searchTerm: string;
  /**
   * Permission keys inherited from the user's assigned roles. They render checked and disabled, and are
   * stripped from the `onSubmit` payload: role assignment is managed separately, and sending them would
   * leave orphan individual grants that survive un-assigning the role.
   */
  lockedPermissions?: Set<string>;
};

type ToggleProps = {
  name: string;
  checked: boolean;
  disabled?: boolean;
  /** Shown on hover when the toggle is disabled. */
  disabledTooltip?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

function Toggle({ name, checked, disabled = false, disabledTooltip, onChange }: ToggleProps) {
  const toggleNode = (
    <span className={classes('toggleWrapper', disabled && 'toggleDisabled')}>
      <SwitchIndicator checked={checked} />

      <input
        className={classes('hiddenCheckbox')}
        id={`checkbox-${name}`}
        type="checkbox"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
    </span>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip
        type="hover"
        position="left"
        hasButton={false}
        customElement={toggleNode}
        information={disabledTooltip}
      />
    );
  }

  return toggleNode;
}

function buildPermissionsConfig(
  permissions: PermissionsMap,
  lockedPermissions: Set<string>,
  lockedTooltip: string,
): Record<string, FieldData<PermissionFormValues>[]> {
  const config: Record<string, FieldData<PermissionFormValues>[]> = {};

  Object.entries(permissions).forEach(([groupName, permissionGroup]) => {
    config[groupName] = Object.entries(permissionGroup).map(([permissionKey, permissionItem]) => {
      const isLocked = lockedPermissions.has(permissionKey);

      return {
        fieldName: permissionKey,
        label: () => permissionItem.label,
        tooltip: () => permissionItem.tooltip,
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<boolean>({});

          return (
            <Toggle
              name={fieldData.name}
              checked={fieldData.value}
              disabled={isLocked}
              disabledTooltip={isLocked ? lockedTooltip : undefined}
              onChange={fieldData.onChange}
            />
          );
        },
      };
    });
  });

  return config;
}

function buildInitialValues(permissions: PermissionsMap, lockedPermissions: Set<string>): PermissionFormValues {
  const initialValues: PermissionFormValues = {};

  Object.values(permissions).forEach((permissionGroup) => {
    Object.entries(permissionGroup).forEach(([permissionKey, permissionItem]) => {
      // A role-granted permission always reads as on, whatever the user's individual layer says.
      initialValues[permissionKey] = lockedPermissions.has(permissionKey) || permissionItem.permitted;
    });
  });

  return initialValues;
}

const EMPTY_LOCKED: Set<string> = new Set();

export default function UserPermissionsForm({
  onSubmit,
  permissions,
  onFormStateChange,
  onAllEnabledChange,
  ref,
  id,
  searchTerm,
  lockedPermissions = EMPTY_LOCKED,
}: UserPermissionsFormProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.UserPermissionsForm' });

  const lockedTooltip = t('lockedTooltip', {
    defaultValue: 'This permission is non-editable since it is determined by the assigned role(s).',
  });

  const initialValues = useMemo(
    () => buildInitialValues(permissions, lockedPermissions),
    [permissions, lockedPermissions],
  );

  const permissionsConfig = useMemo(
    () => buildPermissionsConfig(permissions, lockedPermissions, lockedTooltip),
    [permissions, lockedPermissions, lockedTooltip],
  );

  const { ctx, renderFormElements } = useForm<PermissionFormValues>({
    config: Object.values(permissionsConfig).flat(),
    initialValues,
    onSubmit: (values) => {
      const payload: PermissionFormValues = {};

      Object.entries(values).forEach(([key, value]) => {
        if (!lockedPermissions.has(key)) payload[key] = value;
      });

      onSubmit(payload);
    },
  });

  const hasChanges = useMemo(
    () => JSON.stringify(ctx.values) !== JSON.stringify(initialValues),
    [ctx.values, initialValues],
  );

  const hasAtLeastOnePermission = useMemo(
    () => Object.values(ctx.values).some((value) => value === true),
    [ctx.values],
  );

  useEffect(() => {
    onFormStateChange(hasChanges, hasAtLeastOnePermission);
  }, [hasChanges, hasAtLeastOnePermission, onFormStateChange]);

  const groupStats = useMemo(() => {
    const stats: Record<string, { enabled: number; total: number }> = {};

    Object.entries(permissionsConfig).forEach(([groupName, groupFields]) => {
      stats[groupName] = {
        enabled: groupFields.filter((field) => ctx.values[field.fieldName]).length,
        total: groupFields.length,
      };
    });

    return stats;
  }, [permissionsConfig, ctx.values]);

  // Role-locked permissions are skipped by every bulk toggle: they're already on and immutable.
  function setFieldsEnabled(fields: FieldData<PermissionFormValues>[], enabled: boolean) {
    const updates: PermissionFormValues = {};

    fields.forEach((field) => {
      if (!lockedPermissions.has(field.fieldName)) updates[field.fieldName] = enabled;
    });

    ctx.setValues({ ...ctx.values, ...updates });
  }

  function setGroupEnabled(groupName: string, enabled: boolean) {
    const groupFields = permissionsConfig[groupName];

    if (groupFields) setFieldsEnabled(groupFields, enabled);
  }

  function toggleAll(enabled: boolean) {
    setFieldsEnabled(Object.values(permissionsConfig).flat(), enabled);
  }

  useEffect(() => {
    if (!onAllEnabledChange) return;

    const allFields = Object.values(permissionsConfig).flat();

    onAllEnabledChange(allFields.length > 0 && allFields.every((field) => ctx.values[field.fieldName] === true));
  }, [permissionsConfig, ctx.values, onAllEnabledChange]);

  useImperativeHandle(ref, () => ({ toggleAll }));

  const filteredPermissionsConfig = useMemo(() => {
    if (!searchTerm.trim()) return permissionsConfig;

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered: Record<string, FieldData<PermissionFormValues>[]> = {};

    Object.entries(permissionsConfig).forEach(([groupName, groupFields]) => {
      const groupMatches = groupName.toLowerCase().includes(lowerSearchTerm);

      const matchingPermissions = groupFields.filter((field) =>
        String(field.label?.(ctx) || '')
          .toLowerCase()
          .includes(lowerSearchTerm),
      );

      if (groupMatches || matchingPermissions.length > 0) {
        filtered[groupName] = groupMatches ? groupFields : matchingPermissions;
      }
    });

    return filtered;
  }, [permissionsConfig, searchTerm]);

  const isFiltering = searchTerm.trim().length > 0;
  const hasFilteredResults = Object.keys(filteredPermissionsConfig).length > 0;

  return (
    <form onSubmit={ctx.handleSubmit} id={id} className={classes('formContainer')}>
      {!hasFilteredResults && isFiltering ? (
        <div className={classes('emptyStateWrapper')}>
          <Box flx="col" ai="center" jc="center" gap="sm">
            <MaterialSymbol icon="manage_search" size="90px" color="rgba(45, 45, 45, 0.2)" />
            <Title level="4">{t('permissionNotFound', { defaultValue: 'Permission Not Found' })}</Title>
          </Box>
        </div>
      ) : (
        <CollapsibleList>
          {Object.entries(filteredPermissionsConfig).map(([groupName, groupFields]) => {
            const stats = groupStats[groupName];
            const isGroupAllEnabled = stats.enabled === stats.total;

            // A group whose every permission is role-locked has nothing the user could toggle, so it
            // shows no Enable All / Disable All link.
            const hasAnyUnlocked = groupFields.some((field) => !lockedPermissions.has(field.fieldName));

            return (
              <CollapsibleList.Item key={groupName} defaultOpen={false} open={isFiltering}>
                <CollapsibleList.Trigger>
                  <Box flx="row" jc="sb" ai="center" grow="1">
                    <Box ai="center" gapSize="12px">
                      <Title level="5">{groupName}</Title>

                      <BodyText weight="rg" className={classes('groupStatsText')}>
                        ({stats.enabled}/{stats.total})
                      </BodyText>
                    </Box>

                    {!isFiltering && hasAnyUnlocked && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Box gap="sm">
                          <UtilButton size="medium" onClick={() => setGroupEnabled(groupName, !isGroupAllEnabled)}>
                            {isGroupAllEnabled
                              ? t('disableAll', { defaultValue: 'Disable All' })
                              : t('enableAll', { defaultValue: 'Enable All' })}
                          </UtilButton>
                        </Box>
                      </div>
                    )}
                  </Box>
                </CollapsibleList.Trigger>

                <CollapsibleList.Content>
                  <Box flx="col" gap="md" base="8">
                    {renderFormElements({
                      config: groupFields,
                      override: (fieldData) => (
                        <label className={classes('permissionLabel')} htmlFor={`checkbox-${fieldData.fieldName}`}>
                          <Box gap="sm" ai="center">
                            <BodyText className={classes('permissionLabelText')} weight="md">
                              {fieldData.label}
                            </BodyText>

                            {fieldData.tooltip && (
                              <Tooltip
                                position="topRight"
                                type="hover"
                                information={<BodyText>{fieldData.tooltip}</BodyText>}
                              />
                            )}
                          </Box>

                          {fieldData.template}
                        </label>
                      ),
                    })}
                  </Box>
                </CollapsibleList.Content>
              </CollapsibleList.Item>
            );
          })}
        </CollapsibleList>
      )}
    </form>
  );
}
